/**
 * WEB COPY of LetterCanvasScreen.tsx — picked by the web bundler ahead of the
 * mobile file (`.web.tsx` resolves first); Metro never sees it.
 *
 * Same canvas, same autosave, same Orion entry points. What differs is how
 * the editor takes space: on the phone it fills the screen and scrolls
 * inside; on the web the page scrolls, so the editor grows with the letter
 * (from a comfortable minimum) and the buttons follow beneath it. There is
 * no keyboard to avoid, and the browser's focus ring is replaced by a teal
 * edge on the paper. When LetterCanvasScreen.tsx changes, re-apply the
 * change here.
 */
import {
  clearLetterDraftIfUnchanged,
  readLetterDraft,
  saveLetterInOrder,
  stampLetterDraft,
} from '../../services/letterDraftMirror';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  View,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  TextInput,
  ActivityIndicator,
  Alert,
} from 'react-native';

import { AppText as Text } from '../../components/AppText';
import OrnamentBackground from '../../components/OrnamentBackground';
import { API_URL } from '../../config';
import { getAuthToken } from '../../services/auth/tokenStorage';
import { letterToLovedOnesService } from '../../services/letterToLovedOnesInfo';
import { createLogger } from '../../utils/logger';
import { ms, s } from '../../utils/responsive';

import { OrionHelpSheet, OrionMode } from './OrionHelpSheet';

const logger = createLogger('LetterCanvas');

const AUTOSAVE_DEBOUNCE_MS = 900;
// The editor never shrinks below this; it grows with the letter.
const EDITOR_MIN_HEIGHT = 360;

type CanvasRouteProp = RouteProp<
  { params: { letterId: string; updatedContent?: string } },
  'params'
>;

export const LetterCanvasScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const route = useRoute<CanvasRouteProp>();
  const { letterId, updatedContent } = route.params;

  const [content, setContent] = useState('');
  const [salutation, setSalutation] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [relationship, setRelationship] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [improving, setImproving] = useState(false);
  const [sheetVisible, setSheetVisible] = useState(false);

  const [focused, setFocused] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contentRef = useRef('');
  // Revision of the text in contentRef, from the draft mirror.
  const revRef = useRef(0);
  // react-native-web hands back the <textarea>; its height is set here so
  // the paper, the sheet and the page all grow with the letter.
  const editorRef = useRef<{ style?: { height: string }; scrollHeight?: number } | null>(null);

  useEffect(() => {
    const el = editorRef.current;
    if (!el || !el.style) return;
    el.style.height = 'auto';
    el.style.height = `${Math.max(EDITOR_MIN_HEIGHT, el.scrollHeight ?? 0)}px`;
  }, [content, loading]);

  // Load the letter; an unsent AsyncStorage mirror (crash before the
  // debounce fired) wins over the server copy.
  useEffect(() => {
    const load = async () => {
      try {
        const letter = await letterToLovedOnesService.getLetterToLovedOnesById(letterId);
        // A local draft is restored only once the server has returned this
        // letter to the signed-in person, and only if it is their draft
        // (letterDraftMirror). Without the letter there is nothing to show.
        const mirror = letter ? await readLetterDraft(letterId) : null;
        const serverContent = letter?.letterContent || '';
        const restored = mirror !== null && mirror.text !== serverContent ? mirror.text : serverContent;
        setContent(restored);
        contentRef.current = restored;
        setSalutation(letter?.salutation || letter?.recipientName || '');
        setRecipientName(letter?.recipients?.[0]?.name || letter?.recipientName || '');
        setRelationship(letter?.recipients?.[0]?.relationship || letter?.recipientRelationship || '');
        if (mirror !== null && mirror.text !== serverContent) {
          logger.debug('Restored unsent draft from local mirror');
          revRef.current = mirror.rev;
          pushToServer(mirror.text, mirror.rev);
        } else if (mirror !== null) {
          // The server already has it.
          clearLetterDraftIfUnchanged(letterId, mirror.rev);
        }
      } catch (error) {
        logger.error('Failed to load letter:', error);
        Alert.alert('Error', 'Failed to load your letter. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [letterId]);

  // A result accepted on the Review screen arrives via params.
  useEffect(() => {
    if (updatedContent !== undefined && updatedContent !== null) {
      setContent(updatedContent);
      contentRef.current = updatedContent;
      revRef.current = stampLetterDraft(letterId, updatedContent);
    }
  }, [updatedContent, letterId]);

  const pushToServer = useCallback(
    async (text: string, rev: number) => {
      try {
        // In order with every other save of this letter; skipped when
        // newer text is already saved or queued (saveLetterInOrder).
        const { sent } = await saveLetterInOrder(letterId, rev, () =>
          letterToLovedOnesService.updateLetterToLovedOnes(letterId, {
            letterContent: text,
          }),
        );
        // The server has this revision. The mirror goes only if nothing
        // newer was typed while the save was in flight.
        if (sent) await clearLetterDraftIfUnchanged(letterId, rev);
      } catch (error) {
        // Keep the mirror; it will retry on the next change or save.
        logger.debug('Autosave push failed (mirror kept):', error);
      }
    },
    [letterId],
  );

  const handleChange = (text: string) => {
    setContent(text);
    contentRef.current = text;
    // Mirror immediately — survives force-kill mid-debounce.
    const rev = stampLetterDraft(letterId, text);
    revRef.current = rev;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => pushToServer(text, rev), AUTOSAVE_DEBOUNCE_MS);
  };

  // Flush the debounce when leaving the screen.
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        pushToServer(contentRef.current, revRef.current);
      }
    };
  }, [pushToServer]);

  const words = content.trim() ? content.trim().split(/\s+/).length : 0;
  const canAct = words >= 3;

  const handleSave = async () => {
    if (!canAct || saving) return;
    try {
      setSaving(true);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      const savedRev = revRef.current;
      // After any autosave still in flight, so it cannot land on top.
      await saveLetterInOrder(
        letterId,
        savedRev,
        () =>
          letterToLovedOnesService.updateLetterToLovedOnes(letterId, {
            letterContent: content,
            status: 'saved',
          }),
        { always: true },
      );
      await clearLetterDraftIfUnchanged(letterId, savedRev);
      logger.debug('Letter saved:', letterId);
      navigation.navigate('LetterSaved', { letterId });
    } catch (error) {
      logger.error('Failed to save letter:', error);
      Alert.alert('Error', 'Failed to save your letter. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const runImprove = async () => {
    if (!canAct || improving) return;
    try {
      setImproving(true);
      const token = await getAuthToken();
      if (!token) {
        Alert.alert('Error', 'Please log in to use Orion.');
        return;
      }
      const response = await fetch(`${API_URL}/api/ai/generate/letter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ mode: 'improve', content }),
      });
      const data = await response.json();
      if (data.success && data.content) {
        navigation.navigate('LetterReview', {
          letterId,
          content: data.content,
          mode: 'improve',
        });
      } else {
        Alert.alert('Error', data.error || 'Orion could not help right now. Please try again.');
      }
    } catch (error) {
      logger.error('Improve failed:', error);
      Alert.alert('Error', 'Orion could not help right now. Please try again.');
    } finally {
      setImproving(false);
    }
  };

  const handlePick = (mode: OrionMode) => {
    setSheetVisible(false);
    if (mode === 'improve') {
      runImprove();
      return;
    }
    navigation.navigate('LetterInterview', {
      letterId,
      mode,
      recipientName,
      relationship,
    });
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
      <OrnamentBackground />
        <View style={styles.loadingWrap}>
          <ActivityIndicator color="#FFFFFF" size="large" />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <OrnamentBackground />
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
          accessibilityRole="button"
          accessibilityLabel="Go back"
        >
          <Image
            source={require('../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Text style={styles.headerTitle} numberOfLines={1}>
          To {salutation || 'someone you love'}
        </Text>
        <Text style={styles.wordCount}>{words} words</Text>
      </View>

      <View style={styles.sheet}>
        <TouchableOpacity
          style={styles.orionButton}
          onPress={() => setSheetVisible(true)}
          disabled={improving}
          accessibilityRole="button"
          accessibilityLabel={words < 3 ? 'Ask Orion to help me start' : 'Ask Orion to help with this'}
        >
          {improving ? (
            <ActivityIndicator color="#29BFC0" />
          ) : (
            <Text style={styles.orionButtonText}>
              {words < 3 ? 'Ask Orion to help me start' : 'Ask Orion to help with this'}
            </Text>
          )}
        </TouchableOpacity>

        <View style={[styles.paper, focused && styles.paperFocused]}>
          <Text style={styles.salutationLine}>
            {salutation ? `Dear ${salutation},` : 'Dear…'}
          </Text>
          <TextInput
            ref={editorRef as any}
            style={styles.editor}
            multiline
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            textAlignVertical="top"
            placeholder="Start anywhere. The first sentence is always the hardest — write it badly and fix it later."
            placeholderTextColor="#A0AEC0"
            value={content}
            onChangeText={handleChange}
            accessibilityLabel="Your letter"
          />
        </View>
        <Text style={styles.autosaveNote}>Saved as you type</Text>

        <TouchableOpacity
          style={[styles.improveButton, !canAct && styles.disabled]}
          onPress={runImprove}
          disabled={!canAct || improving}
          accessibilityRole="button"
          accessibilityLabel="Ask Orion to improve"
          accessibilityState={{ disabled: !canAct }}
        >
          <Text style={styles.improveButtonText}>Ask Orion to improve</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.saveButton, (!canAct || saving) && styles.disabled]}
          onPress={handleSave}
          disabled={!canAct || saving}
          accessibilityRole="button"
          accessibilityLabel="Save this letter"
          accessibilityState={{ disabled: !canAct || saving }}
        >
          {saving ? (
            <ActivityIndicator color="#FFFFFF" />
          ) : (
            <Text style={styles.saveButtonText}>Save this letter</Text>
          )}
        </TouchableOpacity>
      </View>

      <OrionHelpSheet
        visible={sheetVisible}
        hasContent={canAct}
        onPick={handlePick}
        onClose={() => setSheetVisible(false)}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  loadingWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  header: {
    height: s(56),
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: ms(16),
  },
  backButton: {
    padding: ms(8),
  },
  backIcon: {
    width: s(28),
    height: s(28),
    tintColor: '#fff',
  },
  headerTitle: {
    flex: 1,
    textAlign: 'center',
    fontSize: ms(17, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    paddingHorizontal: ms(8),
  },
  wordCount: {
    fontSize: ms(12, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    opacity: 0.85,
    width: s(64),
    textAlign: 'right',
  },
  sheet: {
    flexGrow: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(16),
    paddingHorizontal: ms(16),
    paddingBottom: ms(16),
  },
  orionButton: {
    alignItems: 'center',
    paddingVertical: ms(11),
    borderRadius: ms(25),
    borderWidth: ms(1),
    borderColor: '#29BFC0',
    backgroundColor: '#FFFFFF',
    marginBottom: ms(12),
  },
  orionButtonText: {
    color: '#29BFC0',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  paper: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
    padding: ms(15),
  },
  paperFocused: {
    borderColor: '#29BFC0',
  },
  salutationLine: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  editor: {
    minHeight: EDITOR_MIN_HEIGHT,
    fontSize: ms(16, 0.3),
    lineHeight: ms(24, 0.3),
    color: '#2C4364',
    padding: 0,
    // The paper's edge is the focus ring; the browser's default outline is
    // a web-only style, hence the cast.
    ...({ outlineStyle: 'none' } as object),
  },
  autosaveNote: {
    textAlign: 'center',
    fontSize: ms(12, 0.3),
    color: '#A9A9A9',
    marginVertical: ms(8),
  },
  improveButton: {
    alignItems: 'center',
    paddingVertical: ms(13),
    borderRadius: ms(25),
    borderWidth: ms(1.5),
    borderColor: '#29BFC0',
    backgroundColor: '#FFFFFF',
    marginBottom: ms(10),
  },
  improveButtonText: {
    color: '#29BFC0',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  saveButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    alignItems: 'center',
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  disabled: {
    opacity: 0.5,
  },
});

export default LetterCanvasScreen;
